import { useRef, useState } from "react";
import type { Contact } from "../api";
import { Diamond } from "./icons";

interface Props {
  busy: boolean;
  serverErrors: Record<string, string>;
  onSubmit: (contact: Contact) => void;
  onBack: () => void;
  startedAt: number;
  /** How many were asked — the instrument's own count, never assumed. */
  total: number;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function Gate({ busy, serverErrors, onSubmit, onBack, startedAt, total }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const honeypot = useRef<HTMLInputElement>(null);

  const shown = { ...serverErrors, ...errors };

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;

    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Please enter your name.";
    if (!EMAIL_RE.test(email.trim())) next.email = "Please enter a valid email address.";
    if (!company.trim()) next.company = "Please enter your company name.";

    setErrors(next);
    if (Object.keys(next).length > 0) {
      // Focus the first problem so a screen reader and a sighted user land in
      // the same place.
      document.getElementById(`f-${Object.keys(next)[0]}`)?.focus();
      return;
    }

    onSubmit({
      name: name.trim(),
      email: email.trim(),
      company: company.trim(),
      phone: phone.trim(),
      website: honeypot.current?.value ?? "",
      elapsed_ms: Date.now() - startedAt,
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="cat-eyebrow">
        <Diamond />
        Last step
      </div>
      <h1 style={{ fontSize: 25 }}>Your report is ready — where should we send it?</h1>
      <p className="lede" style={{ fontSize: 16 }}>
        You&rsquo;ve answered all {total}. We&rsquo;ll email your scored report, broken down by category,
        with what each score means.
      </p>

      <Field
        id="f-name"
        label="Your name"
        value={name}
        onChange={setName}
        error={shown.name}
        autoComplete="name"
      />
      <Field
        id="f-email"
        label="Email"
        type="email"
        inputMode="email"
        value={email}
        onChange={setEmail}
        error={shown.email}
        autoComplete="email"
      />
      <Field
        id="f-company"
        label="Company"
        value={company}
        onChange={setCompany}
        error={shown.company}
        autoComplete="organization"
      />
      <Field
        id="f-phone"
        label="Phone"
        optional
        type="tel"
        inputMode="tel"
        value={phone}
        onChange={setPhone}
        error={shown.phone}
        autoComplete="tel"
      />

      {/* Honeypot: real people never fill this; bots usually do. */}
      <div className="hp" aria-hidden="true">
        <label htmlFor="f-website">Website</label>
        <input id="f-website" name="website" ref={honeypot} tabIndex={-1} autoComplete="off" />
      </div>

      <div className="nav">
        <button type="button" className="btn btn-ghost" onClick={onBack} disabled={busy}>
          Back
        </button>
        <div className="spacer" />
        <button type="submit" className="btn" disabled={busy}>
          {busy ? "Sending…" : "Send my report"}
        </button>
      </div>

      <p className="gate-assure">
        <span style={{ color: "var(--citron-ink)", flexShrink: 0, marginTop: 2 }}>
          <Diamond />
        </span>
        <span>
          Your answers stay between you and Mastery Partners. No spam, and you can ask us to delete
          your results at any time.
        </span>
      </p>
    </form>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  optional,
  type = "text",
  inputMode,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  optional?: boolean;
  type?: string;
  inputMode?: "email" | "tel" | "text";
  autoComplete?: string;
}) {
  return (
    <div className="field-row">
      <label htmlFor={id}>
        {label} {optional && <span className="opt-note">(optional)</span>}
      </label>
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
      />
      {error && (
        <span className="field-err" id={`${id}-err`}>
          {error}
        </span>
      )}
    </div>
  );
}
